import React, {useEffect, useState} from 'react'
import {CachedProduct, getCachedProducts} from '../productCache'
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import {checkInStockItems, createTransferRequest} from '../transferUtils'
import {useAuth} from '../../auth/auth'
import {ApiError} from '../../common/ApiError'
import {webEvents} from '../../common/WebEvents'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'


const CreateTransferRequest = () => {
    const navigate = useNavigate()
    const {getTruck} = useAuth()
    const truckName = getTruck()
    const dispatch = useDispatch()
    const [selectedItem, setSelectedItem] = useState<CachedProduct | null>(null)
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [filterText, setFilterText] = useState('')
    const [selectedReason, setSelectedReason] = useState<string | null>(null)
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [open, setOpen] = useState(false)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const [inStock, setInStock] = useState(false)
    const [loading, setLoading] = useState(false)
    const [additionalInfo, setAdditionalInfo] = useState('')

    const transferReasons = [
        'Truck is out of stock and I have an order to complete for a customer.',
        'Truck is out of stock and item is on backorder at the warehouse.',
        'Truck is out of stock and the item is not in my weekly replenishment.',
        'I have orders that need the item and not enough in stock.'
    ]

    useEffect(() => {
        const fetchProducts = async () => {
            let cachedProducts = await getCachedProducts()
            setProducts(
                Object.values(cachedProducts)
            )
        }
        fetchProducts()
    }, [])

    const enableRequestButton = selectedItem != null && savedQty != null && selectedReason != null

    const inStockDialogText = () => {
        return (
            <Typography
                component="div"
                variant="body1"
                align="left"
                sx={{
                    whiteSpace: 'pre-line',
                    fontWeight: 'medium'
                }}
            >
                {`The item you're requesting is in-stock on your truck. Submit request anyway?

                ${selectedItem?.sku || 'No Item Selected'}, Qty: ${savedQty || 0}`}
            </Typography>
        )
    }

    const checkStock = async () => {
        //Check for item in stock /inventory/locations/snapshot/?locations={truckName}&skus={selectedItem}
        try {
            if (selectedItem) {
                const inStockItems = await checkInStockItems({locations: truckName, skus: selectedItem.sku})
                if (inStockItems.data.length > 0) {
                    setInStock(true)
                }
            }
        } catch (error) {
            if (error instanceof ApiError) {
                webEvents.error(
                    error.message,
                    {'review_page': 'Counts in Review'},
                    error.stack
                )
                const apiError = error as unknown as ApiError // Type assertion
                const causeMessage =
                    (apiError as any)?.cause?.messages?.[0] ||
                    (apiError as any)?.messages?.[0] ||
                    'Unknown error'

                dispatch(createError({
                    type: (apiError as any)?.type || 'error',
                    message: causeMessage
                }))
            } else {
                // Handle unexpected error type
                dispatch(createError({
                    type: 'Fetching Location Snapshot Error',
                    message: 'An unexpected error occurred'
                }))
            }
        }
    }

    const processSubmitTransferRequest = () => {
        if (truckName && enableRequestButton) {
            let combinedReason = additionalInfo.trim()
                ? `${selectedReason} Additional Info: ${additionalInfo.trim()}`
                : selectedReason
            createTransferRequest(truckName, selectedItem.sku, savedQty, combinedReason).then(() => {
                handleClose()
                navigate('/inventory/transfers', {state: {submissionSuccess: true}, replace: true})
            }).catch(error => {
                webEvents.error(
                    error.message,
                    {'creating transfer for truck': truckName},
                    error.stack
                )
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }
    }

    const confirmSubmit = () => {
        setOpenConfirmDialog(true)
        setLoading(true)
        checkStock().finally(() => {
            setLoading(false)
        })
    }

    const handleClose = () => {
        setInStock(false)
        setOpenConfirmDialog(false)
    }

    const handleConfirm = () => {
        processSubmitTransferRequest()
        handleClose()
        window.sessionStorage.setItem('receiveSuccess', 'true')
        navigate('/inventory/transfer-requests')
    }


    return (
        <>
            <Dialog
                open={openConfirmDialog}
                onClose={handleClose}>
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            minWidth: '400px'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <DialogTitle>Confirm Submission</DialogTitle>
                        <DialogContent>
                            <Typography component="div">
                                {inStock ? inStockDialogText() :
                                    'You are about to confirm a transfer request\n' +
                                    'Please confirm if you wish to continue.'
                                }
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 2
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                color="error"
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                color="primary"
                                variant="contained"
                            >
                                Confirm
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            <Box
                sx={{
                    flex: 1,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 3,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxWidth: '600px',
                    width: '100%',
                    margin: '0 auto',
                    gap: 3
                }}
            >
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                        color: 'primary.main'
                    }}
                >
                    Request Item Transfer
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: 3
                    }}
                >
                    <Autocomplete
                        disablePortal
                        id="sampleSelection"
                        options={products}
                        getOptionLabel={option => `${option.sku} - ${option.model || ''}`}
                        value={selectedItem}
                        onChange={(_e, newValue) => setSelectedItem(newValue)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                required
                                label="Select Item"
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <TextField
                        required
                        type="number"
                        label="Quantity"
                        variant="outlined"
                        value={savedQty ?? ''}
                        onChange={e => setSavedQty(Number(e.target.value))}
                        fullWidth
                        InputProps={{
                            inputProps: {
                                max: 10,
                                min: 1
                            }
                        }}
                        error={savedQty !== null && savedQty > 10} //disable request btn if > 10??
                        helperText={savedQty !== null && savedQty > 10
                            ? 'Quantity Limit exceeded: 10'
                            : 'Maximum quantity is 10'}
                        color={savedQty !== null && savedQty > 10 ? 'error' : 'primary'}
                    />

                    <Autocomplete
                        disablePortal
                        id="transferReason"
                        options={transferReasons}
                        value={selectedReason}
                        onChange={(_e, newValue) => setSelectedReason(newValue)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                required
                                label="Select Transfer Reason"
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <TextField
                        label="Additional Info"
                        multiline
                        rows={3}
                        value={additionalInfo}
                        onChange={(e) => {
                            if (e.target.value.length <= 1024) {
                                setAdditionalInfo(e.target.value)
                            }
                        }}
                        helperText={`${1024 - additionalInfo.length} characters remaining`}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 2,
                        mt: 2
                    }}
                >
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        sx={{mr: 1}}
                        onClick={() => navigate('/inventory/transfer-requests')}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ml: 1}}
                        disabled={!enableRequestButton}
                        onClick={() => confirmSubmit()}
                    >
                        Request Transfer
                    </Button>
                </Box>
            </Box>
        </>
    )
}

export default CreateTransferRequest
